import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Download, RefreshCw, Trash2 } from "lucide-react";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { apiUrl } from "../../../common/api/config";
import { ReactComponent as GoogleSheetsIcon } from "../../../common/assets/img/vectors/google_sheets_icon.svg";
import PageHeader from "../../../common/components/PageHeader";
import { useProject } from "../../../common/contexts/ProjectContext";
import { getFiles, getRemapData } from "../../api/files";
import ColumnMappingModal from "../../components/ColumnMappingModal";
import FileUploadInput from "../../components/FileUploadInput";
import ViewFileModal from "../../components/ViewFileModal";

const ImportData = () => {
  const { t } = useTranslation("sources");
  const [files, setFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  // Column mapping modal state
  const [showColumnMappingModal, setShowColumnMappingModal] = useState(false);
  const [pendingFileData, setPendingFileData] = useState(null);

  // Get current user role from project context
  const { currentUserRole } = useProject();
  const isOwner = currentUserRole === "owner";

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await getFiles();
        setFiles(response);
      } catch (error) {
        console.error("Error fetching files:", error);
      }
    };

    fetchFiles();
  }, []);

  const handleOpenCsvViewer = async (file) => {
    try {
      const response = await fetch(apiUrl + file.file + "?t=" + Date.now());
      const text = await response.text();
      const parsedData = Papa.parse(text, {
        header: true,
        dynamicTyping: true,
      }).data;
      setCsvData(parsedData);
      setSelectedFile(file);
      setShowModal(true);
    } catch (error) {
      console.error("Error reading the CSV file:", error);
      alert("There was an error parsing the file.");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCsvData([]);
  };

  const handleFileUploaded = (fileData) => {
    setPendingFileData(fileData);
    setShowColumnMappingModal(true);
  };

  const handleMappingConfirmed = (response) => {
    // Update the file in the list (works for both new uploads and remaps)
    setFiles((prevFiles) => {
      const exists = prevFiles.some((f) => f.id === response.file.id);
      if (exists) {
        return prevFiles.map((f) =>
          f.id === response.file.id ? response.file : f,
        );
      }
      return [response.file, ...prevFiles];
    });
    setPendingFileData(null);
  };

  const handleRemap = async (file) => {
    try {
      const remapData = await getRemapData(file.id);
      setPendingFileData(remapData);
      setShowColumnMappingModal(true);
    } catch (error) {
      console.error("Error getting remap data:", error);
      alert("Error: " + error.message);
    }
  };

  const handleCloseColumnMappingModal = () => {
    setShowColumnMappingModal(false);
    setPendingFileData(null);
  };

  const getFileStatusChip = (file) => {
    if (file.is_processed) {
      return (
        <Chip
          label={t("manualImport.status.processed")}
          color="success"
          size="small"
        />
      );
    } else {
      return (
        <Chip
          label={t("manualImport.status.pendingMapping")}
          color="warning"
          size="small"
        />
      );
    }
  };

  // Show access denied message for non-owners
  if (!isOwner) {
    return (
      <div className="py-3">
        <PageHeader
          title={t("manualImport.pageTitle")}
          description={t("manualImport.pageDescription")}
        />

        <Alert severity="warning">
          <AlertTitle>{t("manualImport.accessRestricted")}</AlertTitle>
          <Typography>{t("manualImport.accessRestrictedMessage")}</Typography>
        </Alert>
      </div>
    );
  }

  return (
    <div className="py-3">
      <PageHeader
        title={t("manualImport.pageTitle")}
        description={t("manualImport.pageDescription")}
      />

      <FileUploadInput
        setFiles={setFiles}
        onFileUploaded={handleFileUploaded}
      />

      {files.length > 0 && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h6" sx={{ pb: 3 }}>
            {t("manualImport.uploadedFiles")}
          </Typography>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 650 }} aria-label="uploaded files table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("manualImport.table.fileName")}</TableCell>
                  <TableCell>{t("manualImport.table.status")}</TableCell>
                  <TableCell>{t("manualImport.table.uploadedAt")}</TableCell>
                  <TableCell>{t("manualImport.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow
                    key={file.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {file.name}
                    </TableCell>
                    <TableCell>{getFileStatusChip(file)}</TableCell>
                    <TableCell>
                      {new Date(file.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {file.is_processed && (
                          <>
                            <IconButton
                              onClick={() => handleOpenCsvViewer(file)}
                              aria-label="view file"
                            >
                              <GoogleSheetsIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleRemap(file)}
                              aria-label="remap file"
                              title="Remap columns"
                            >
                              <RefreshCw size={20} />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          component="a"
                          href={apiUrl + file.file}
                          aria-label="download file"
                        >
                          <Download size={20} />
                        </IconButton>
                        <IconButton
                          component={Link}
                          to={`/sources/manual-import/${file.id}/delete`}
                          aria-label="delete file"
                          sx={{ color: "error.main" }}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {showModal && (
        <ViewFileModal
          csvData={csvData}
          selectedFile={selectedFile}
          handleCloseModal={handleCloseModal}
          setFiles={setFiles}
        />
      )}

      <ColumnMappingModal
        open={showColumnMappingModal}
        onClose={handleCloseColumnMappingModal}
        fileData={pendingFileData}
        onMappingConfirmed={handleMappingConfirmed}
      />
    </div>
  );
};

export default ImportData;
